import { useState, useEffect } from 'react';

import { Tabs } from '@/components/Tabs';
import { useFormatText } from '@/hooks/useFormatText';
import usePageTitle from '@/hooks/usePageTitle';
import AccountSecurity from '@/pages/common/settingTabs/AccountSecurity';
import LanguageRegion from '@/pages/common/settingTabs/LanguageRegion';
import { useSettingsStore } from '@/store/useSettingsStore';

import ParentSubscription from './components/ParentSubscription';
import Preference from './components/preference';
import ProfileSettings from './components/ProfileSettings';

// ─── Types ───────────────────────────────────────────────────────────────────

type TSettingsTab =
  | 'profile'
  | 'preference'
  | 'language'
  | 'security'
  | 'subscription';

// ─── Tab Options ─────────────────────────────────────────────────────────────

const useSettingsTabs = () => {
  const { formatText } = useFormatText();

  return [
    {
      label: formatText('settings.tabs.profile-settings'),
      value: 'profile' as const,
    },
    {
      label: formatText('settings.tabs.preferences'),
      value: 'preference' as const,
    },
    {
      label: formatText('settings.tabs.language-region'),
      value: 'language' as const,
    },
    {
      label: formatText('settings.tabs.account-security'),
      value: 'security' as const,
    },
    {
      label: formatText('settings.tabs.subscription'),
      value: 'subscription' as const,
    },
  ];
};

// ─── Tab Content Map ─────────────────────────────────────────────────────────

const TAB_CONTENT: Record<TSettingsTab, React.FC> = {
  profile: ProfileSettings,
  preference: Preference,
  language: LanguageRegion,
  security: AccountSecurity,
  subscription: ParentSubscription,
};

// ─── Settings Page ───────────────────────────────────────────────────────────

const ParentSettings = () => {
  const { formatText } = useFormatText();
  const { setTitle } = usePageTitle();
  const [activeTab, setActiveTab] = useState<TSettingsTab>('profile');
  const tabOptions = useSettingsTabs();
  const ActiveComponent = TAB_CONTENT[activeTab];
  const language = useSettingsStore((s) => s.settings.language);

  useEffect(() => {
    setTitle(formatText('settings.page-title'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  return (
    <div className="flex flex-col gap-6 py-6">
      {/* Tabs */}
      <Tabs options={tabOptions} value={activeTab} onChange={setActiveTab} />

      {/* Tab Content */}
      <div className="mt-2 mx-auto w-full flex items-center justify-center">
        <ActiveComponent />
      </div>
    </div>
  );
};

export default ParentSettings;
